import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/solid';

const Status = () => {
  const services = [
    { name: 'API', status: 'operational', uptime: '99.9%' },
    { name: 'Instagram Sync', status: 'operational', uptime: '99.8%' },
    { name: 'Google Drive Integration', status: 'operational', uptime: '99.9%' },
    { name: 'Payment Processing', status: 'operational', uptime: '100%' },
    { name: 'CDN', status: 'operational', uptime: '99.9%' },
  ];

  const incidents = [
    {
      date: '2024-01-10',
      title: 'Scheduled Maintenance',
      description: 'System upgrade completed successfully',
      status: 'resolved',
      duration: '2 hours'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return <CheckCircleIcon className="h-5 w-5 text-green-400" />;
      case 'degraded':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />;
      case 'down':
        return <XCircleIcon className="h-5 w-5 text-red-400" />;
      default:
        return <CheckCircleIcon className="h-5 w-5 text-green-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'bg-green-400';
      case 'degraded':
        return 'bg-yellow-400';
      case 'down':
        return 'bg-red-400';
      default:
        return 'bg-green-400';
    }
  };

  const overallStatus = services.every(s => s.status === 'operational') ? 'operational' : 'degraded';

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <Navbar />
      
      <main className="flex-grow pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              System Status
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Current status of Vaulto services and infrastructure
            </p>
          </div>

          {/* Overall Status */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 mb-12">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`h-4 w-4 rounded-full ${getStatusColor(overallStatus)} mr-4`}></div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {overallStatus === 'operational' ? 'All Systems Operational' : 'Some Systems Degraded'}
                  </h2>
                  <p className="text-gray-300 mt-1">Last checked: {new Date().toLocaleString()}</p>
                </div>
              </div>
              <button className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors">
                Refresh
              </button>
            </div>
          </div>

          {/* Service Status */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 mb-12">
            <h3 className="text-xl font-bold text-white mb-6">Service Status</h3>
            <div className="space-y-4">
              {services.map((service, index) => (
                <div key={index} className="flex items-center justify-between py-4 border-b border-white/10 last:border-0">
                  <div className="flex items-center">
                    {getStatusIcon(service.status)}
                    <span className="ml-3 text-white font-medium">{service.name}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-300">Uptime: {service.uptime}</span>
                    <span className={`text-sm capitalize ${
                      service.status === 'operational' ? 'text-green-400' : 
                      service.status === 'degraded' ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {service.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Incidents */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <h3 className="text-xl font-bold text-white mb-6">Recent Incidents</h3>
            {incidents.length > 0 ? (
              <div className="space-y-4">
                {incidents.map((incident, index) => (
                  <div key={index} className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-lg font-semibold text-white">{incident.title}</h4>
                        <p className="text-gray-300 mt-1">{incident.description}</p>
                        <div className="flex items-center mt-3 space-x-4 text-sm">
                          <span className="text-gray-400">{incident.date}</span>
                          <span className="text-gray-400">Duration: {incident.duration}</span>
                          <span className={`capitalize ${
                            incident.status === 'resolved' ? 'text-green-400' : 'text-yellow-400'
                          }`}>
                            {incident.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-300 text-center py-8">No recent incidents</p>
            )}
          </div>

          {/* Subscribe to Updates */}
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-pink-500/20 to-purple-600/20 backdrop-blur-lg rounded-3xl p-8 border border-white/20 max-w-2xl mx-auto">
              <h3 className="text-xl font-bold text-white mb-4">
                Subscribe to Status Updates
              </h3>
              <p className="text-gray-300 mb-6">
                Get notified about system maintenance and incidents
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="your@email.com"
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
                <button className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-xl text-white font-medium transition-all duration-200">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer variant="dark" />
    </div>
  );
};

export default Status; 